"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseDecimal = parseDecimal;
exports.decimalToMinor = decimalToMinor;
exports.convertSoldToReceive = convertSoldToReceive;
exports.convertReceiveToSold = convertReceiveToSold;
exports.effectiveSoldLimits = effectiveSoldLimits;
const DECIMAL = /^(\d+)(?:\.(\d+))?$/;
function parseDecimal(value) {
    const text = typeof value === 'number' ? String(value) : value.trim();
    const match = DECIMAL.exec(text);
    if (!match)
        throw new Error(`Expected a plain decimal, got "${text}"`);
    const [, whole, fraction = ''] = match;
    return { digits: BigInt(`${whole}${fraction}`), scale: fraction.length };
}
function pow10(exponent) {
    return 10n ** BigInt(exponent);
}
function assertDecimals(decimals, name) {
    if (!Number.isInteger(decimals) || decimals < 0)
        throw new Error(`Expected non-negative integer ${name}, got ${decimals}`);
}
/** `"1734.28"` with 2 decimals → `"173428"`. Extra precision is floored; missing precision is padded. */
function decimalToMinor(value, decimals) {
    assertDecimals(decimals, 'decimals');
    const { digits, scale } = parseDecimal(value);
    const minor = scale > decimals ? digits / pow10(scale - decimals) : digits * pow10(decimals - scale);
    return minor.toString();
}
/**
 * `receiveMinor = floor(soldAtomic × rate × 10^toDecimals / 10^fromDecimals)`.
 * `rate` is "units of the destination per one unit of the source", as the exchanger quotes it.
 */
function convertSoldToReceive(soldAtomic, fromDecimals, rate, toDecimals) {
    assertDecimals(fromDecimals, 'fromDecimals');
    assertDecimals(toDecimals, 'toDecimals');
    const sold = BigInt(soldAtomic);
    const r = parseDecimal(rate);
    if (r.digits === 0n)
        throw new Error('Rate must be greater than zero');
    const numerator = sold * r.digits * pow10(toDecimals);
    const denominator = pow10(r.scale) * pow10(fromDecimals);
    return (numerator / denominator).toString();
}
function receiveToSold(receiveMinor, toDecimals, rate, fromDecimals, rounding) {
    assertDecimals(toDecimals, 'toDecimals');
    assertDecimals(fromDecimals, 'fromDecimals');
    const receive = BigInt(receiveMinor);
    const r = parseDecimal(rate);
    if (r.digits === 0n)
        throw new Error('Rate must be greater than zero');
    const numerator = receive * pow10(r.scale) * pow10(fromDecimals);
    const denominator = r.digits * pow10(toDecimals);
    const quotient = numerator / denominator;
    const exact = numerator % denominator === 0n;
    return (rounding === 'up' && !exact ? quotient + 1n : quotient).toString();
}
/** The inverse, rounded up: selling the result must yield at least `receiveMinor`. */
function convertReceiveToSold(receiveMinor, toDecimals, rate, fromDecimals) {
    return receiveToSold(receiveMinor, toDecimals, rate, fromDecimals, 'up');
}
/**
 * The sold-side window once the destination's own `minTo`/`maxTo` are mapped through the rate:
 * the stricter bound wins on each side. The destination minimum is rounded up (selling `min` must
 * reach `minTo`), the destination maximum down (selling `max` must not exceed `maxTo`). This is
 * the one window the Mini App shows and the API enforces; the exchanger checks both sides itself.
 */
function effectiveSoldLimits(direction, fromDecimals) {
    const { rate, toDecimals } = direction;
    const minViaTo = BigInt(receiveToSold(direction.minTo, toDecimals, rate, fromDecimals, 'up'));
    const maxViaTo = BigInt(receiveToSold(direction.maxTo, toDecimals, rate, fromDecimals, 'down'));
    const minFrom = BigInt(direction.minFrom);
    const maxFrom = BigInt(direction.maxFrom);
    return {
        min: (minViaTo > minFrom ? minViaTo : minFrom).toString(),
        max: (maxViaTo < maxFrom ? maxViaTo : maxFrom).toString(),
    };
}
//# sourceMappingURL=exchange-math.js.map